import { RemixBrowser } from '@remix-run/react'
import { startTransition } from 'react'
import { hydrateRoot } from 'react-dom/client'
import { clearWorkoutDraft } from './utils/workout-draft.ts'

if (ENV.MODE === 'production' && ENV.SENTRY_DSN) {
	void import('./utils/monitoring.client.tsx').then(({ init }) => init())
}

// A mutation that fails at the network layer (e.g. the connection drops mid-save)
// is otherwise swallowed by Remix — the submitting route component can even
// unmount before it settles, so there's no reliable place inside a form to catch
// it. Wrap fetch once, globally, to flag any POST that fails to reach the server
// (excluding intentional AbortController cancellations), then re-throw so Remix's
// own handling proceeds unchanged. We dispatch an event rather than calling
// `toast()` here because this entry chunk's `sonner` instance is separate from
// the app chunk that mounts the Toaster; the root listens and raises the toast.
const originalFetch = window.fetch
window.fetch = async (input, init) => {
	const requestUrl =
		typeof input === 'string'
			? input
			: input instanceof URL
				? input.href
				: input instanceof Request
					? input.url
					: ''
	const requestMethod = (
		init?.method ?? (input instanceof Request ? input.method : 'GET')
	).toUpperCase()
	try {
		const res = await originalFetch(input, init)
		// Once a new-workout save actually reaches the server (any non-error
		// status), the localStorage draft is no longer needed — drop it. Validation
		// errors are 4xx and skipped here (the user may still want their work);
		// network failures throw and never get this far.
		if (res.status < 400 && requestMethod === 'POST' && requestUrl) {
			try {
				const url = new URL(requestUrl, window.location.origin)
				if (url.pathname.includes('/workouts/new')) {
					const date = url.searchParams.get('date')
					if (date) clearWorkoutDraft(date)
				}
			} catch {
				// Ignore URL parse failures — clearing the draft is best-effort.
			}
		}
		return res
	} catch (error) {
		const isAbort = error instanceof DOMException && error.name === 'AbortError'
		if (requestMethod === 'POST' && !isAbort) {
			window.dispatchEvent(new Event('app:network-error'))
		}
		throw error
	}
}

// Ensure hydration happens after document is ready
function hydrate() {
	startTransition(() => {
		hydrateRoot(document, <RemixBrowser />)
	})
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', hydrate)
} else {
	hydrate()
}
